import { useEffect, useMemo, useState, useCallback } from "react";
import { TopBar } from "../components/TopBar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { api, STATUS_LABELS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Plus, Trash2, Edit3, ShieldCheck, ShieldAlert, Users as UsersIcon,
  Lock, ChevronRight, KeyRound,
} from "lucide-react";

/** Small helpers */
const groupBy = (arr, key) =>
  arr.reduce((acc, item) => {
    (acc[item[key]] = acc[item[key]] || []).push(item);
    return acc;
  }, {});

export default function RolesPage() {
  const { user: me } = useAuth();
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState({ permissions: [], statuses: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // role being edited
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        api.get("/roles"),
        api.get("/permissions/catalog"),
      ]);
      setRoles(r.data);
      setCatalog(c.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load roles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (role) => {
    if (role.is_system) return toast.error("System roles can't be deleted");
    if (role.user_count > 0) return toast.error(`${role.user_count} user(s) still use this role — reassign them first`);
    if (!window.confirm(`Delete role "${role.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/roles/${role.id}`);
      toast.success("Role deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const isAdmin = me?.role === "admin";

  return (
    <div className="min-h-screen bg-zinc-50">
      <TopBar />
      <main className="mx-auto max-w-[1200px] px-4 sm:px-6 py-6">
        <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter text-zinc-950">
              Roles &amp; Permissions
            </h1>
            <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
              Create custom roles and pick exactly what each one can do. System roles can be
              tuned but not deleted. Changes take effect on a user&apos;s next request.
            </p>
          </div>
          {isAdmin && (
            <Button
              onClick={() => setCreating(true)}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="create-role-button"
            >
              <Plus className="h-4 w-4 mr-2" /> New role
            </Button>
          )}
        </div>

        <div className="grid gap-3">
          {loading && (
            <div className="p-10 text-center text-zinc-400 bg-white border border-zinc-200 rounded-md">
              Loading roles…
            </div>
          )}
          {!loading && roles.map((r) => (
            <RoleCard
              key={r.id}
              role={r}
              onEdit={() => setEditing(r)}
              onDelete={() => remove(r)}
              canManage={isAdmin}
              permissionCatalog={catalog.permissions}
            />
          ))}
        </div>

        <p className="text-[11px] text-zinc-500 mt-4 flex items-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5" />
          Admin role permissions are locked to full access to prevent accidental lockout.
        </p>
      </main>

      <RoleDialog
        open={creating || !!editing}
        editing={editing}
        catalog={catalog}
        onClose={() => { setCreating(false); setEditing(null); }}
        onSaved={() => { setCreating(false); setEditing(null); load(); }}
      />
    </div>
  );
}

/* ─────────── Role card summary ─────────── */
function RoleCard({ role, onEdit, onDelete, canManage, permissionCatalog }) {
  const totalPerms = permissionCatalog.length || Object.keys(role.permissions || {}).length;
  const granted = Object.values(role.permissions || {}).filter(Boolean).length;
  const isAdmin = role.key === "admin";

  return (
    <div
      className="bg-white border border-zinc-200 rounded-md p-4 hover:border-zinc-300 transition-colors"
      data-testid={`role-row-${role.key}`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading text-lg font-bold tracking-tight text-zinc-950">
              {role.name}
            </h3>
            <span className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 bg-zinc-100 px-1.5 py-0.5 rounded">
              {role.key}
            </span>
            {role.is_system && (
              <Badge variant="outline" className="text-[10px] border-zinc-300 text-zinc-600">
                <Lock className="h-2.5 w-2.5 mr-1" /> System
              </Badge>
            )}
            {isAdmin && (
              <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700 bg-emerald-50">
                <ShieldCheck className="h-2.5 w-2.5 mr-1" /> Full access
              </Badge>
            )}
            <span className="text-xs text-zinc-500 inline-flex items-center gap-1 ml-auto sm:ml-2">
              <UsersIcon className="h-3 w-3" /> {role.user_count} user{role.user_count === 1 ? "" : "s"}
            </span>
          </div>
          {role.description && (
            <p className="text-sm text-zinc-600 mt-1">{role.description}</p>
          )}
          <div className="mt-2 flex items-center gap-3 text-[11px] text-zinc-500">
            <span>
              <span className="font-semibold text-zinc-800">{granted}</span> / {totalPerms} permissions
            </span>
            <span>·</span>
            <span>
              <span className="font-semibold text-zinc-800">{role.allowed_statuses?.length || 0}</span> allowed statuses
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {canManage && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={onEdit}
                className="h-8 border-zinc-200"
                data-testid={`edit-role-${role.key}`}
              >
                <Edit3 className="h-3 w-3 mr-1.5" /> Edit
              </Button>
              {!role.is_system && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onDelete}
                  className="h-8 border-zinc-200 text-rose-600 hover:bg-rose-50"
                  disabled={role.user_count > 0}
                  data-testid={`delete-role-${role.key}`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────── Create/Edit dialog ─────────── */
const emptyForm = {
  key: "",
  name: "",
  description: "",
  permissions: {},
  allowed_statuses: [],
};

function RoleDialog({ open, editing, catalog, onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const permGroups = useMemo(() => groupBy(catalog.permissions || [], "group"), [catalog.permissions]);
  const groupOrder = useMemo(() => {
    const seen = new Set();
    const order = [];
    (catalog.permissions || []).forEach((p) => {
      if (!seen.has(p.group)) { order.push(p.group); seen.add(p.group); }
    });
    return order;
  }, [catalog.permissions]);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        key: editing.key,
        name: editing.name,
        description: editing.description || "",
        permissions: { ...(editing.permissions || {}) },
        allowed_statuses: [...(editing.allowed_statuses || [])],
      });
    } else {
      // For new roles, seed defaults from read_only (least-privilege)
      const rest = {};
      (catalog.permissions || []).forEach((p) => { rest[p.key] = false; });
      setForm({ ...emptyForm, permissions: rest });
    }
  }, [editing, open, catalog.permissions]);

  const isSystem = editing?.is_system || false;
  const isAdminRole = editing?.key === "admin";

  const togglePerm = (k, v) => {
    if (isAdminRole && (k === "users.manage" || k === "roles.manage")) {
      // Force-locked admin critical perms
      toast.message("Admin's users.manage & roles.manage are always on to prevent lockout.");
      return;
    }
    setForm((f) => ({ ...f, permissions: { ...f.permissions, [k]: v } }));
  };

  const toggleStatus = (s, v) => {
    setForm((f) => {
      const set = new Set(f.allowed_statuses);
      if (v) set.add(s); else set.delete(s);
      return { ...f, allowed_statuses: Array.from(set) };
    });
  };

  const bulkGroup = (group, v) => {
    setForm((f) => {
      const perms = { ...f.permissions };
      (permGroups[group] || []).forEach((p) => {
        if (isAdminRole && (p.key === "users.manage" || p.key === "roles.manage")) return;
        perms[p.key] = v;
      });
      return { ...f, permissions: perms };
    });
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) return toast.error("Role name required");
    if (!editing && !form.key.trim()) return toast.error("Role key required");
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/roles/${editing.id}`, {
          name: form.name.trim(),
          description: form.description.trim(),
          permissions: form.permissions,
          allowed_statuses: form.allowed_statuses,
        });
        toast.success(`Role "${form.name}" updated`);
      } else {
        await api.post("/roles", {
          key: form.key.trim(),
          name: form.name.trim(),
          description: form.description.trim(),
          permissions: form.permissions,
          allowed_statuses: form.allowed_statuses,
        });
        toast.success(`Role "${form.name}" created`);
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const grantedCount = Object.values(form.permissions).filter(Boolean).length;
  const totalCount = catalog.permissions?.length || 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="role-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tighter flex items-center gap-2">
            {editing ? (
              <>
                <Edit3 className="h-4 w-4" /> Edit role: {editing.name}
                {isSystem && <Lock className="h-3.5 w-3.5 text-zinc-400" />}
              </>
            ) : (
              <><Plus className="h-4 w-4" /> New role</>
            )}
          </DialogTitle>
          <DialogDescription>
            {isSystem
              ? "This is a system role — you can tune its permissions but the key & delete are locked."
              : editing
                ? "Adjust permissions and allowed status transitions."
                : "Create a custom role. Users assigned to this role get exactly the permissions you tick here."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5 py-2">
          {/* Identity */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-zinc-500">Role key *</Label>
              <Input
                value={form.key}
                onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
                disabled={!!editing}
                placeholder="e.g. warehouse_lead"
                required
                data-testid="role-key-input"
                className="mt-1.5 font-mono"
              />
              <p className="text-[10px] text-zinc-400 mt-0.5">
                Lowercase, snake_case. Used internally — cannot be changed later.
              </p>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-zinc-500">Display name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Warehouse Lead"
                required
                data-testid="role-name-input"
                className="mt-1.5"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-wide text-zinc-500">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="One sentence describing when to use this role…"
                rows={2}
                className="mt-1.5 resize-none"
                data-testid="role-description-input"
              />
            </div>
          </div>

          {/* Permissions */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="font-heading text-sm font-bold tracking-tight text-zinc-950">
                  Permissions
                </h3>
                <p className="text-[11px] text-zinc-500">
                  {grantedCount} of {totalCount} enabled
                </p>
              </div>
              {isAdminRole && (
                <div className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 flex items-center gap-1">
                  <ShieldCheck className="h-3 w-3" /> Admin gets full access always
                </div>
              )}
            </div>
            <div className="space-y-3 border border-zinc-200 rounded-md">
              {groupOrder.map((group) => {
                const groupPerms = permGroups[group] || [];
                const onCount = groupPerms.filter((p) => form.permissions[p.key]).length;
                return (
                  <div key={group} className="border-b border-zinc-100 last:border-b-0">
                    <div className="flex items-center justify-between bg-zinc-50 px-3 py-2">
                      <div className="flex items-center gap-2">
                        <ChevronRight className="h-3.5 w-3.5 text-zinc-400" />
                        <span className="font-semibold text-xs uppercase tracking-wider text-zinc-700">
                          {group}
                        </span>
                        <span className="text-[10px] text-zinc-500 font-mono">
                          {onCount}/{groupPerms.length}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => bulkGroup(group, true)}
                          className="text-[10px] text-zinc-500 hover:text-zinc-900 uppercase tracking-wider"
                          data-testid={`bulk-on-${group}`}
                        >
                          Enable all
                        </button>
                        <span className="text-zinc-300">|</span>
                        <button
                          type="button"
                          onClick={() => bulkGroup(group, false)}
                          className="text-[10px] text-zinc-500 hover:text-zinc-900 uppercase tracking-wider"
                          data-testid={`bulk-off-${group}`}
                        >
                          Disable all
                        </button>
                      </div>
                    </div>
                    <div className="divide-y divide-zinc-100">
                      {groupPerms.map((p) => {
                        const locked = isAdminRole && (p.key === "users.manage" || p.key === "roles.manage");
                        return (
                          <label
                            key={p.key}
                            className="flex items-start justify-between gap-3 px-3 py-2.5 hover:bg-zinc-50 cursor-pointer"
                            data-testid={`perm-row-${p.key}`}
                          >
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm font-semibold text-zinc-900">{p.label}</span>
                                {locked && <Lock className="h-3 w-3 text-zinc-400" />}
                              </div>
                              <p className="text-[11px] text-zinc-500 leading-snug mt-0.5">{p.description}</p>
                              <span className="text-[10px] font-mono text-zinc-400">{p.key}</span>
                            </div>
                            <Switch
                              checked={!!form.permissions[p.key]}
                              onCheckedChange={(v) => togglePerm(p.key, v)}
                              disabled={locked}
                              data-testid={`perm-toggle-${p.key}`}
                            />
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Allowed statuses */}
          <div>
            <h3 className="font-heading text-sm font-bold tracking-tight text-zinc-950 mb-1">
              Allowed status transitions
            </h3>
            <p className="text-[11px] text-zinc-500 mb-2">
              Which statuses can users in this role set on a record. Leave everything unchecked to prevent
              status changes entirely.
            </p>
            <div className="flex flex-wrap gap-2">
              {(catalog.statuses || []).map((s) => {
                const active = form.allowed_statuses.includes(s.key);
                return (
                  <button
                    type="button"
                    key={s.key}
                    onClick={() => toggleStatus(s.key, !active)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      active
                        ? "bg-zinc-950 text-white border-zinc-950"
                        : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"
                    }`}
                    data-testid={`status-toggle-${s.key}`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="role-submit"
            >
              {saving ? "Saving…" : editing ? "Save changes" : "Create role"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
